import { useState, useMemo, useEffect, useCallback } from "react"

export default <T,>(key: string) => {
	const [localValue, setLocalValue] = useState<string | null>(
		typeof window !== "undefined" ? window.localStorage.getItem(key) : null
	)

	// Listen for storage changes from other tabs/windows AND same-tab updates
	useEffect(() => {
		if (typeof window === "undefined") return;

		// Handle cross-tab storage events
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === key) {
				setLocalValue(e.newValue);
			}
		};

		// Handle same-tab updates via custom event
		const handleCustomStorageChange = (e: CustomEvent<{ key: string; value: string | null }>) => {
			if (e.detail.key === key) {
				setLocalValue(e.detail.value);
			}
		};

		window.addEventListener("storage", handleStorageChange);
		window.addEventListener("local-storage-change", handleCustomStorageChange as EventListener);

		return () => {
			window.removeEventListener("storage", handleStorageChange);
			window.removeEventListener("local-storage-change", handleCustomStorageChange as EventListener);
		};
	}, [key]);

	const value = useMemo<T>(
		() => (localValue && localValue !== "undefined" ? JSON.parse(localValue) : undefined),
		[localValue]
	)

	const setValue = useCallback((val: T) => {
		if (val === undefined || val === null) {
			window.localStorage.removeItem(key)
			setLocalValue(null)
			// Dispatch custom event for same-tab synchronization
			window.dispatchEvent(new CustomEvent("local-storage-change", { detail: { key, value: null } }));
		} else {
			const encodedValue = JSON.stringify(val)
			window.localStorage.setItem(key, encodedValue)
			setLocalValue(encodedValue)
			// Dispatch custom event for same-tab synchronization
			window.dispatchEvent(new CustomEvent("local-storage-change", { detail: { key, value: encodedValue } }));
		}
	}, [key])

	return [value, setValue] as const
}
